import {
  Alert,
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import {
  RelatedNotesResponse,
  RelatedTasksNotesDto,
} from '@dispatch/shared/services';
import { NoteListItem } from '../NoteListItem';

export interface RelatedTasksDrawerProps {
  data: RelatedNotesResponse;
  onClose: () => void;
  /** Claim number rendered above each task header so the user can confirm
   * which claim the related tasks belong to. */
  claimNumber?: string;
}

/**
 * Build the per-task section header. The date format borrows the single piece
 * we reuse from `Timeline.tsx` (line 196 — `toLocaleString()`); everything
 * else is plain string assembly with no lookup tables or icons.
 */
const renderTaskHeader = (task: RelatedTasksNotesDto): string => {
  const dateStr = task.relevantDateUtc
    ? new Date(task.relevantDateUtc).toLocaleString()
    : null;
  const type = task.taskType ?? '';
  const status = task.status ?? '';
  if (dateStr && type && status) return `${type} - ${status} ${dateStr}`;
  if (dateStr && type) return `${type} ${dateStr}`;
  if (type && status) return `${type} - ${status}`;
  return type || status || task.taskId;
};

/**
 * RelatedTasksDrawer - slide-out panel listing related-task notes grouped by
 * task. Section headers use plain `Date#toLocaleString()` — no icons and no
 * status lookup tables. Renders a truncation indicator (the count of notes
 * actually shown) at the bottom when the server flagged the result as truncated.
 */
export const RelatedTasksDrawer: React.FC<RelatedTasksDrawerProps> = ({
  data,
  onClose,
  claimNumber,
}) => {
  const groups = data.results ?? [];
  // The server doesn't return the cap, only a `truncated` flag, so report the
  // actual number of notes rendered rather than a hardcoded ceiling.
  const shownNoteCount = groups.reduce(
    (sum, group) => sum + (group.notes?.length ?? 0),
    0
  );

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      data-testid="related-tasks-drawer"
      PaperProps={{ sx: { minWidth: '40vw' } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: '12px 16px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1">Related notes</Typography>
        <IconButton
          onClick={onClose}
          data-testid="related-tasks-drawer-close"
          aria-label="Close related notes"
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {groups.length === 0 && (
          <Box
            sx={{ padding: '24px 16px', textAlign: 'center' }}
            data-testid="related-tasks-empty-state"
          >
            <Typography variant="body2" color="text.secondary">
              No related notes.
            </Typography>
          </Box>
        )}
        {groups.map((group) => (
          <Box
            key={group.taskId}
            data-testid={`related-task-group-${group.taskId}`}
            sx={{ padding: '8px 0' }}
          >
            <Box
              sx={{
                padding: '8px 16px',
                backgroundColor: 'action.hover',
              }}
              data-testid="related-task-group-header"
            >
              {claimNumber && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="related-task-group-claim"
                  sx={{ display: 'block' }}
                >
                  Claim {claimNumber}
                </Typography>
              )}
              <Typography
                variant="subtitle2"
                component="h3"
                sx={{ fontWeight: 600 }}
              >
                {renderTaskHeader(group)}
              </Typography>
            </Box>
            {group.notes.map((n) => (
              <NoteListItem key={n.noteId} note={n} />
            ))}
          </Box>
        ))}
      </Box>
      {data.truncated && (
        <Alert
          severity="info"
          data-testid="related-tasks-truncation-banner"
          sx={{ margin: '12px 16px' }}
        >
          {`Showing the first ${shownNoteCount} notes. Refine your search to see the rest.`}
        </Alert>
      )}
    </Drawer>
  );
};
